'use client';
import { useState, useEffect, useCallback, useRef, Suspense } from 'react';
import { useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import ScoreCards from '@/components/dashboard/ScoreCards';
import type { OverviewData } from '@/components/dashboard/ScoreCards';
import ComplianceTable from '@/components/dashboard/ComplianceTable';
import type { TechCompliance } from '@/components/dashboard/ComplianceTable';
import ActivityFeed from '@/components/dashboard/ActivityFeed';
import type { ActivityEvent } from '@/components/dashboard/ActivityFeed';
import TrendChart from '@/components/dashboard/TrendChart';
import Leaderboard from '@/components/dashboard/Leaderboard';
import CoachingReport from '@/components/dashboard/CoachingReport';
import dynamic from 'next/dynamic';
const CustomerResponsesPanel = dynamic(
  () => import('@/components/dashboard/CustomerResponsesPanel'),
  { ssr: false, loading: () => <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-pulse h-32" /> }
);
import PhotoLightbox from '@/components/dashboard/PhotoLightbox';
import ToastNotifications from '@/components/dashboard/ToastNotifications';
import type { Toast } from '@/components/dashboard/ToastNotifications';

// Today's date in Central Time as YYYY-MM-DD.
// Must NOT round-trip through `new Date(localeString).toISOString()` — that parses
// the CT wall clock in the BROWSER's timezone and then converts back to UTC, which
// rolls the date forward a day from 7pm CT onward for anyone on Central time.
// en-CA formats as YYYY-MM-DD and is evaluated directly in the requested zone.
function todayCentral(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
}

function DashboardContent() {
  const [mounted, setMounted] = useState(false);
  const { data: session, status } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();

  // Filters — always initialize with defaults for consistent SSR/client render
  const [date, setDate] = useState(todayCentral());
  const [branch, setBranch] = useState('All');
  const [leader, setLeader] = useState('All');

  // Sync initial values from URL params on mount (client-only)
  useEffect(() => {
    const d = searchParams.get('date');
    const b = searchParams.get('branch');
    const l = searchParams.get('leader');
    if (d) setDate(d);
    if (b) setBranch(b);
    if (l) setLeader(l);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Data state
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [techs, setTechs] = useState<TechCompliance[]>([]);
  const [techsLoading, setTechsLoading] = useState(true);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);

  // Lightbox
  const [lightboxPhotos, setLightboxPhotos] = useState<string[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Toasts
  const [toasts, setToasts] = useState<Toast[]>([]);
  const lastEventIdRef = useRef<string>('');

  // Auth redirect
  useEffect(() => {
    if (status === 'unauthenticated') router.push('/login');
  }, [status, router]);

  // Sync filters to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (date !== todayCentral()) params.set('date', date);
    if (branch !== 'All') params.set('branch', branch);
    if (leader !== 'All') params.set('leader', leader);
    const qs = params.toString();
    const newUrl = qs ? `/dashboard?${qs}` : '/dashboard';
    window.history.replaceState(null, '', newUrl);
  }, [date, branch, leader]);

  // Build query params helper
  const buildParams = useCallback((extra?: Record<string, string>) => {
    const p = new URLSearchParams({ date });
    if (branch !== 'All') p.set('branch', branch);
    if (leader !== 'All') p.set('leader', leader);
    if (extra) Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  }, [date, branch, leader]);

  // Fetch overview
  const fetchOverview = useCallback(() => {
    setOverviewLoading(true);
    fetch(`/api/dashboard/overview?${buildParams()}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setOverview(d))
      .catch(() => setOverview(null))
      .finally(() => setOverviewLoading(false));
  }, [buildParams]);

  // Fetch compliance
  const fetchCompliance = useCallback(() => {
    setTechsLoading(true);
    fetch(`/api/dashboard/compliance?${buildParams({ period: 'today' })}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setTechs(d.techs || []))
      .catch(() => setTechs([]))
      .finally(() => setTechsLoading(false));
  }, [buildParams]);

  // Fetch activity
  const fetchActivity = useCallback((silent = false) => {
    if (!silent) setEventsLoading(true);
    fetch(`/api/dashboard/activity?${buildParams({ limit: '50' })}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        const newEvents: ActivityEvent[] = d.events || [];
        setEvents(newEvents);

        // Check for new flagged events (toast)
        if (lastEventIdRef.current && newEvents.length > 0) {
          const lastIdx = newEvents.findIndex(e => e.id === lastEventIdRef.current);
          const newOnes = lastIdx > 0 ? newEvents.slice(0, lastIdx) : lastIdx === -1 ? newEvents.slice(0, 3) : [];
          newOnes
            .filter(e => e.type === 'text_missed')
            .forEach(e => {
              setToasts(prev => [...prev, {
                id: e.id,
                message: `⚠️ ${e.techName} missed text for ${e.customerName}`,
                type: 'error' as const,
                timestamp: Date.now(),
              }]);
            });
        }
        if (newEvents.length > 0) lastEventIdRef.current = newEvents[0].id;
      })
      .catch(() => {})
      .finally(() => { if (!silent) setEventsLoading(false); });
  }, [buildParams]);

  // Initial + filter-change fetch
  useEffect(() => {
    if (status !== 'authenticated') return;
    fetchOverview();
    fetchCompliance();
    fetchActivity();
  }, [status, fetchOverview, fetchCompliance, fetchActivity]);

  // Auto-refresh every 2 minutes
  useEffect(() => {
    if (status !== 'authenticated') return;
    const interval = setInterval(() => {
      fetchOverview();
      fetchCompliance();
      fetchActivity(true);
    }, 120000);
    return () => clearInterval(interval);
  }, [status, fetchOverview, fetchCompliance, fetchActivity]);

  // Mark as mounted (prevents SSR/client hydration mismatch)
  useEffect(() => setMounted(true), []);

  // Activity poll every 60 seconds for toasts
  useEffect(() => {
    if (status !== 'authenticated') return;
    const interval = setInterval(() => fetchActivity(true), 60000);
    return () => clearInterval(interval);
  }, [status, fetchActivity]);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const openLightbox = useCallback((photos: string[], index: number) => {
    setLightboxPhotos(photos);
    setLightboxIndex(index);
  }, []);

  if (!mounted || status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[#D86018] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <div className="text-gray-500 text-sm">Loading dashboard...</div>
        </div>
      </div>
    );
  }

  if (status === 'unauthenticated') return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardHeader
        date={date}
        branch={branch}
        leader={leader}
        userName={session?.user?.name || session?.user?.email || 'User'}
        onDateChange={setDate}
        onBranchChange={setBranch}
        onLeaderChange={setLeader}
      />

      <main className="max-w-[1600px] mx-auto px-4 py-6 space-y-6">
        {/* Row 1: Score Cards */}
        <ScoreCards data={overview} loading={overviewLoading} />

        {/* Row 2: Compliance Table + Activity Feed */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <ComplianceTable
              techs={techs}
              loading={techsLoading}
              date={date}
              branch={branch}
              leader={leader}
              onPhotoClick={openLightbox}
            />
          </div>
          <div className="lg:col-span-2">
            <ActivityFeed events={events} loading={eventsLoading} />
          </div>
        </div>

        {/* Row 3: Trend Chart + Leaderboard */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TrendChart branch={branch} leader={leader} />
          <Leaderboard />
        </div>

        {/* Row 4: Customer Responses Panel */}
        <CustomerResponsesPanel />

        {/* Row 5: Coaching Report */}
        <CoachingReport date={date} branch={branch} />

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 py-4">
          Miller Proof Operations Dashboard · Data refreshes every 2 minutes
        </div>
      </main>

      {/* Overlays */}
      <ToastNotifications toasts={toasts} onDismiss={dismissToast} />
      {lightboxPhotos.length > 0 && (
        <PhotoLightbox
          photos={lightboxPhotos}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxPhotos([])}
          onNavigate={setLightboxIndex}
        />
      )}
    </div>
  );
}

export default function DashboardPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-500">Loading dashboard...</div></div>}>
      <DashboardContent />
    </Suspense>
  );
}
